import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import config from "../../Config";
import SetupMenu from "./SetupMenu";

const allowedExt = [".doc", ".docx", ".xls", ".xlsx", ".pdf", ".zip"];

const ClassSyllabusFiles = () => {
  const { classId } = useParams();
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const className = decodeURIComponent(new URLSearchParams(window.location.search).get("className") || "");

  useEffect(() => {
    document.title = "Class Syllabus Files";
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const loadFiles = () => {
    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "ClassSyllabus/GetClassSyllabusByClass",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        Class_Id: Number(classId)
      }
    })
      .then((res) => {
        if (res.data && res.data.status_code === 1) {
          const list = res.data.ListofRecords || [];
          setRows(list);
          setTotalRecords(list.length);
        } else {
          setRows([]);
          setTotalRecords(0);
          toast.error(res.data?.status_message || "Could not load syllabus files.");
        }
      })
      .catch(() => {
        setRows([]);
        setTotalRecords(0);
        toast.error("Could not load syllabus files.");
      })
      .finally(() => setLoading(false));
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const onFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const invalid = selected.find((f) => {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      return !allowedExt.includes(ext);
    });
    if (invalid) {
      toast.error("Only Word, Excel, PDF and ZIP files are allowed.");
      e.target.value = "";
      setFiles([]);
      return;
    }
    setFiles(selected);
  };

  const uploadFiles = async () => {
    if (!files.length) {
      toast.error("Please select at least one file.");
      return;
    }

    setUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      try {
        const content = await toBase64(file);
        const res = await axios({
          method: "post",
          url: config.base_url + "ClassSyllabus/AddClassSyllabusFile",
          headers: { "Content-Type": "application/json" },
          data: {
            AccessKey,
            UserID,
            MemberType,
            Class_Id: Number(classId),
            SyllabusDetails: notes,
            FileName: file.name,
            FileContent: content
          }
        });
        if (res.data && res.data.status_code === 1) {
          ok += 1;
        } else {
          toast.error(`${file.name}: ${res.data?.status_message || "Upload failed."}`);
        }
      } catch (e) {
        toast.error(`${file.name}: Upload failed.`);
      }
    }
    setUploading(false);
    setFiles([]);
    setNotes("");
    const input = document.getElementById("syllabus-file-input");
    if (input) input.value = "";
    if (ok > 0) toast.success(`${ok} file(s) uploaded successfully.`);
    loadFiles();
  };

  const deleteFile = (id) => {
    if (!window.confirm("Are you sure you want to delete this syllabus file?")) return;
    axios({
      method: "post",
      url: config.base_url + "ClassSyllabus/DeleteClassSyllabusFile",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, ID: id }
    })
      .then((res) => {
        if (res.data && res.data.status_code === 1) {
          toast.success(res.data.status_message || "Deleted successfully.");
          loadFiles();
        } else {
          toast.error(res.data?.status_message || "Delete failed.");
        }
      })
      .catch(() => toast.error("Delete failed."));
  };

  const getPublicFileUrl = (fileName) => `${config.root}ClassSyllabus/${fileName}`;

  return (
    <>
      <SetupMenu />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/Dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/Setup/SetupDashboard">Setup</Link></li>
                  <li className="breadcrumb-item"><Link to="/Setup/ManageClassSyllabus">Manage Class Syllabus</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Class Syllabus Files</li>
                </ol>
              </nav>
              <div className="page-title">
                Class Syllabus Files {className ? `(${className})` : ""}
              </div>
            </div>
            <div className="col-md-6">
              <div className="page-right-actions">
                <Link to="/Setup/ManageClassSyllabus" className="button button-white">
                  Back
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card mb-2">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Attach Files (multiple)</label>
                    <input
                      id="syllabus-file-input"
                      type="file"
                      className="form-control"
                      multiple
                      accept=".doc,.docx,.xls,.xlsx,.pdf,.zip"
                      onChange={onFileChange}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Details / Notes</label>
                    <input
                      type="text"
                      className="form-control"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional notes for uploaded files"
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group mt-md-4">
                    <button
                      className="button button-primary w-100"
                      onClick={uploadFiles}
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </div>
              </div>
              <div className="row align-items-center mt-1">
                <div className="col-md-6"></div>
                <div className="col-md-6">
                  <div className="counting text-end">
                    <span>{totalRecords} </span> Records Found!
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body table-wrap">
              <table className="table table-theme table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>File</th>
                    <th>Details</th>
                    <th>Created</th>
                    <th>Last Updated</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <Spinner animation="border" role="status"></Spinner>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">No syllabus files uploaded yet.</td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.ID}>
                        <td>{r.ID}</td>
                        <td>
                          {r.SyllabusFileAttached}
                          {r.SyllabusFileAttached ? (
                            <a href={getPublicFileUrl(r.SyllabusFileAttached)} target="_blank" rel="noreferrer" className="ms-2">
                              View
                            </a>
                          ) : null}
                        </td>
                        <td>{r.SyllabusDetails || "-"}</td>
                        <td>{r.Created_Date ? String(r.Created_Date).replace("T", " ").substring(0, 19) : "-"}</td>
                        <td>{r.LastUpdated ? String(r.LastUpdated).replace("T", " ").substring(0, 19) : "-"}</td>
                        <td className="text-center">
                          <button className="button button-danger button-sm" onClick={() => deleteFile(r.ID)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClassSyllabusFiles;
